import { useState, useEffect, useRef } from "react";
import toastError from "../../errors/toastError";
import { format, sub } from 'date-fns'
import api from "../../services/api";
import { readPageCache, writePageCache } from "../../utils/pageCache";

const useTickets = ({
  searchParam,
  tags,
  users,
  leadSources,
  pageNumber,
  status,
  date,
  updated_at,
  showAll,
  queueIds,
  crmStageIds,
  withUnreadMessages,
  whatsappIds,
  statusFilter,
  forceSearch,
  userFilter,
  sortTickets,
  searchOnMessages,
  active = true
}) => {
  const userString = localStorage.getItem("userId") || "";
  const cacheKey = (pageNumber === 1 && !searchParam && status) 
    ? `ticketsList:${status}:${showAll}:${userString}` 
    : null;

  const [loading, setLoading] = useState(() => {
    if (cacheKey && readPageCache(cacheKey)?.length > 0) {
      return false;
    }
    return true;
  });
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState(() => {
    if (cacheKey) {
      return readPageCache(cacheKey) || [];
    }
    return [];
  });
  const [count, setCount] = useState(0);
  const isMounted = useRef(true);
  const firstRender = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return;
    }

    // Se for o primeiro carregamento, não esperamos 500ms
    // Se não houver busca textual, usamos um delay menor para navegação fluida
    const delay = firstRender.current ? 0 : (searchParam ? 500 : 100);
    
    let isCurrent = true;

    setLoading(true);
    
    const delayDebounceFn = setTimeout(() => {
      const fetchTickets = async () => {
        const token = localStorage.getItem("token");
        if (!token || token === "undefined" || token === "null") {
          setLoading(false);
          return;
        }

        if (userFilter === undefined || userFilter === null) {
            const mapToIds = (items) => {
                if (!Array.isArray(items)) return [];
                return items.map(item => (typeof item === 'object' && item !== null) ? (item.id || item.status) : item);
            };

            try {            
              const params = {
                searchParam,
                pageNumber,
                tags: JSON.stringify(mapToIds(tags)),
                users: JSON.stringify(mapToIds(users)),
                leadSources: JSON.stringify(mapToIds(leadSources)),
                status, // Aba
                date,
                updated_at,
                showAll,
                queueIds: JSON.stringify(queueIds || []),
                crmStageIds: JSON.stringify(crmStageIds || []),
                withUnreadMessages,
                whatsapps: JSON.stringify(mapToIds(whatsappIds)),
                statusFilter: JSON.stringify(mapToIds(statusFilter)),
                sortTickets,
                searchOnMessages
              };

              // Limpeza de nulos para evitar 404 por excesso de campos vazios em alguns servidores
              Object.keys(params).forEach(key => {
                if (params[key] === "" || params[key] === "[]" || params[key] === undefined || params[key] === null) {
                  delete params[key];
                }
              });

              const { data } = await api.get("/tickets", { params });
              // console.log(`[useTickets] FETCHED /tickets. status=${status} count=${data.tickets?.length}`);
            
              if (isMounted.current && isCurrent) {
                setTickets(data.tickets);
                setHasMore(data.hasMore);
                setCount(data.count);
                setLoading(false);
                firstRender.current = false;
                if (cacheKey && Array.isArray(data.tickets)) {
                  writePageCache(cacheKey, data.tickets);
                }
              }
            } catch (err) {
              if (isMounted.current && isCurrent) {
                setLoading(false);
                toastError(err);
              }
            }
        } else {
          try {
            const {data} = await api.get("/dashboard/moments", {
              params: {
                status,
                showAll,
                queueIds,
                dateStart: format(sub(new Date(), { days: 30 }), 'yyyy-MM-dd'),
                dateEnd: format(new Date(), 'yyyy-MM-dd'),
                user_id: userFilter
              }
            })

            if (isMounted.current && isCurrent) {
                let tickets = data.filter(item => item.user_id == userFilter);            
                setTickets(tickets);
                setHasMore(false);
                setLoading(false);
                firstRender.current = false;
            }
          } catch (err) {
            if (isMounted.current && isCurrent) {
                setLoading(false);
                toastError(err);
            }
          }
        }
      };
      fetchTickets();
    }, delay);

    return () => {
        isCurrent = false;
        clearTimeout(delayDebounceFn);
    };
  }, [
    searchParam,
    tags,
    users,
    leadSources,
    pageNumber,
    status,
    date,
    updated_at,
    showAll,
    queueIds,
    crmStageIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    userFilter,
    active
  ]);

  return { tickets, loading, hasMore, count };
};

export default useTickets;
